import React, {useMemo} from "react";
import {Modal} from "react-bootstrap";

export function DayAttendanceModal({show, onHide, selectedDate, attendanceData, branches, workers}) {

    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Filter attendance for the selected day
    const dayAttendance = useMemo(() => {
        const startOfDay = new Date(selectedDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);

        return attendanceData.filter(att => {
            const attDate = new Date(att.attendanceDate);
            return attDate >= startOfDay && attDate <= endOfDay;
        });
    }, [selectedDate, attendanceData]);

    // Group attendance by branch
    const attendanceByBranch = useMemo(() => {
        const grouped = {};

        dayAttendance.forEach(att => {
            const branchId = att.branchId;
            if (!grouped[branchId]) {
                const branch = branches.find(b => b.id === branchId);
                grouped[branchId] = {
                    branchId: branchId,
                    branchName: att.branchName || branch?.name || 'Unknown Branch',
                    dealerName: branch?.dealerName || '',
                    workers: []
                };
            }
            grouped[branchId].workers.push(att);
        });

        // Sort workers within each branch by name
        Object.values(grouped).forEach(branch => {
            branch.workers.sort((a, b) => (a.workerName || '').localeCompare(b.workerName || ''));
        });

        // Convert to array and sort by branch name
        return Object.values(grouped).sort((a, b) => a.branchName.localeCompare(b.branchName));
    }, [dayAttendance, branches]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
            scrollable
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-calendar-day me-2 text-primary"></i>
                    Attendance Details
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
                {/* Date Header */}
                <div className="bg-light p-4 border-bottom">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-1 fw-bold text-gray-900">{formatDate(selectedDate)}</h5>
                            <span className="text-gray-600 fs-7">
                                Total Workers Present: <span className="fw-bold text-primary">{dayAttendance.length}</span>
                            </span>
                        </div>
                        <div className="badge badge-lg badge-primary">
                            {attendanceByBranch.length} {attendanceByBranch.length === 1 ? 'Branch' : 'Branches'}
                        </div>
                    </div>
                </div>

                {/* Attendance by Branch */}
                <div className="p-4">
                    {attendanceByBranch.length === 0 ? (
                        <div className="text-center py-10">
                            <i className="bi bi-inbox fs-1 text-gray-400 mb-3 d-block"></i>
                            <h5 className="text-gray-700 mb-2">No Attendance Records</h5>
                            <p className="text-gray-600 fs-7">
                                No workers were marked present on this day.
                            </p>
                        </div>
                    ) : (
                        <div className="accordion" id="branchAccordion">
                            {attendanceByBranch.map((branch, branchIndex) => (
                                <div key={branch.branchId} className="accordion-item border rounded mb-3">
                                    <h2 className="accordion-header" id={`heading${branchIndex}`}>
                                        <button
                                            className="accordion-button fw-bold bg-light collapsed"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target={`#collapse${branchIndex}`}
                                            aria-expanded="true"
                                            aria-controls={`collapse${branchIndex}`}
                                        >
                                            <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="symbol symbol-40px symbol-circle">
                                                        <span className="symbol-label bg-light-primary text-primary fw-bold">
                                                            <i className="bi bi-building fs-4 text-primary"></i>
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <div className="text-gray-900 fw-bold fs-5">
                                                            {branch.branchName}
                                                        </div>
                                                        {branch.dealerName && (
                                                            <div className="text-gray-600 fs-8">
                                                                {branch.dealerName}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="badge badge-primary badge-lg">
                                                    {branch.workers.length} Workers
                                                </div>
                                            </div>
                                        </button>
                                    </h2>
                                    <div
                                        id={`collapse${branchIndex}`}
                                        className="accordion-collapse collapse"
                                        aria-labelledby={`heading${branchIndex}`}
                                        data-bs-parent="#branchAccordion"
                                    >
                                        <div className="accordion-body p-0">
                                            <div className="table-responsive">
                                                <table className="table table-row-bordered table-hover align-middle gs-0 gy-3 mb-0">
                                                    <thead>
                                                        <tr className="fw-bold text-muted bg-light">
                                                            <th className="ps-4 rounded-start">#</th>
                                                            <th>Worker Name</th>
                                                            <th>Marked By</th>
                                                            <th className="rounded-end">Time</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {branch.workers.map((worker, workerIndex) => (
                                                            <tr key={worker.id}>
                                                                <td className="ps-4">
                                                                    <span className="text-gray-700 fw-bold">
                                                                        {workerIndex + 1}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex align-items-center">
                                                                        <div className="symbol symbol-35px symbol-circle me-3">
                                                                            <span className="symbol-label bg-light-success text-success fw-bold">
                                                                                {worker.workerName?.charAt(0).toUpperCase() || 'W'}
                                                                            </span>
                                                                        </div>
                                                                        <div className="d-flex flex-column">
                                                                            <span className="text-gray-900 fw-bold">
                                                                                {worker.workerName || 'Unknown'}
                                                                            </span>
                                                                            <span className="text-gray-600 fs-8">
                                                                                ID: {worker.workerId}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <span className="text-gray-700">
                                                                        {worker.supervisorName || 'N/A'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className="badge badge-light-primary">
                                                                        {worker.createdOn
                                                                            ? new Date(worker.createdOn).toLocaleTimeString([], {
                                                                                hour: '2-digit',
                                                                                minute: '2-digit'
                                                                            })
                                                                            : 'N/A'
                                                                        }
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="bg-light">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="text-gray-600 fs-7">
                        <i className="bi bi-info-circle me-1"></i>
                        Click on branch to expand/collapse worker list
                    </div>
                    <button className="btn btn-light" onClick={onHide}>
                        Close
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
